/**
 * Formulário de Criação de URA (Unidade de Resposta Audível)
 * VigosCloud - URA System
 * 
 * Permite criar nova URA com menu principal e extensões
 */

import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { accessLocalStorage, readAllTenant, exportSelectAllQueues, getContextsByTenant, getIntegrationsByAccountcode } from '../../../services';
import * as URAApi from '../../../services/uraApi';

import Loading from '../../Loading/LoadingModule';
import MenuConfiguration from './components/MenuConfiguration';
import ExtensionManager from './components/ExtensionManager';
import MohConfiguration from '../shared/MohConfiguration';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSave,
  faArrowLeft,
  faPhoneVolume,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';

import '../../../libs/bulma.min.css';
import './URAForm.css';

function URACreate() {
  const history = useHistory();
  
  const [loading, setLoading] = useState(false);
  const [userTenant, setUserTenant] = useState('');
  const [hasWhatsAppIntegration, setHasWhatsAppIntegration] = useState(false);
  const [availableQueues, setAvailableQueues] = useState([]);
  const [availableContexts, setAvailableContexts] = useState([]);
  
  // Estado do formulário
  const [values, setValues] = useState({
    tenant: '',
    context_name: '',
    description: '',
    
    // Menu Principal
    menu_extension: 'menu',
    menu_audio_enabled: false,
    menu_audio_file: '',
    menu_audio_type: '',
    menu_audio_accountcode: '',
    menu_audio_method: 'background',
    menu_gotoiftime_rules: [],
    
    // WaitExten
    waitexten_timeout: 10,
    waitexten_destination_type: 'hangup',
    waitexten_destination: '',
    waitexten_overflows: [],
    
    // Ciclo
    cycle_enabled: false,
    max_retries: 3,
    
    // Music on Hold
    moh_enabled: false,
    moh_class: '',
    
    // Extensões
    extensions: []
  });
  
  // Alert state
  const [alert, setAlert] = useState({
    show: false,
    type: '',
    message: ''
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const user = await accessLocalStorage.getUserLocalStorage();
      if (!user?.user?.idtenant) {
        history.push('/');
        return;
      }

      const tenantId = user.user.idtenant;
      setUserTenant(tenantId);
      
      // Buscar dados do tenant da API
      let tenantName = tenantId;
      try {
        console.log('🔍 [URA-CREATE] Buscando dados do tenant:', tenantId);
        const tenantsData = await readAllTenant();
        
        if (Array.isArray(tenantsData)) {
          const tenantInfo = tenantsData.find(t => t.short_name === tenantId);
          if (tenantInfo && tenantInfo.name) {
            tenantName = tenantInfo.name;
            console.log('✅ [URA-CREATE] Nome do tenant encontrado:', tenantName);
          } else {
            console.log('⚠️ [URA-CREATE] Tenant não encontrado na API, usando ID:', tenantId);
          }
        }
      } catch (error) {
        console.warn('⚠️ [URA-CREATE] Erro ao buscar tenant, usando ID:', error);
      }
      
      // Gerar context_name padrão: ura-{nome-normalizado}-{tenant}
      const normalizedName = tenantName
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')  // Remove acentos
        .replace(/[^a-z0-9-]/g, '')        // Remove caracteres especiais (mantém apenas letras, números e hífen)
        .replace(/\s+/g, '')               // Remove espaços
        .substring(0, 30);                 // Limita tamanho
      
      const defaultContextName = `ura-${normalizedName}-${tenantId}`;
      
      setValues(prev => ({
        ...prev,
        tenant: tenantId,
        context_name: defaultContextName,
        menu_audio_accountcode: tenantId
      }));

      console.log('📝 [URA-CREATE] Context name padrão gerado:', defaultContextName);
      
      // Buscar filas disponíveis
      try {
        console.log('🔍 [URA-CREATE] Buscando filas...');
        const queuesData = await exportSelectAllQueues();
        console.log('📦 [URA-CREATE] Dados de filas recebidos:', queuesData);
        
        let queues = [];
        if (Array.isArray(queuesData)) {
          queues = queuesData;
        } else if (queuesData && Array.isArray(queuesData.data)) {
          queues = queuesData.data;
        } else if (queuesData && queuesData.queues) {
          queues = queuesData.queues;
        }
        
        console.log('📋 [URA-CREATE] Array de filas:', queues);
        console.log('📋 [URA-CREATE] Exemplo de fila:', queues[0]);
        
        // Filtrar apenas filas do tenant atual (usar accountcodequeue como no IncomingCallsCreate)
        const tenantQueues = tenantId.includes('vgs') 
          ? queues 
          : queues.filter(q => {
              const accountcode = q.accountcodequeue || q.accountcode;
              console.log(`🔍 Verificando fila: ${q.namequeue || q.name}, accountcode: ${accountcode}, tenantId: ${tenantId}`);
              return tenantId.includes(accountcode);
            });
        
        setAvailableQueues(tenantQueues);
        console.log(`✅ [URA-CREATE] ${tenantQueues.length} filas carregadas para o tenant ${tenantId}:`, tenantQueues);
      } catch (error) {
        console.error('⚠️ [URA-CREATE] Erro ao buscar filas:', error);
      }
      
      // Buscar contextos disponíveis (para DISA)
      try {
        console.log('🔍 [URA-CREATE] Buscando contextos...');
        const contextsData = await getContextsByTenant(tenantId);
        console.log('📦 [URA-CREATE] Dados de contextos recebidos:', contextsData);
        
        let contexts = [];
        if (Array.isArray(contextsData)) {
          contexts = contextsData;
        } else if (contextsData && Array.isArray(contextsData.data)) {
          contexts = contextsData.data;
        } else if (contextsData && contextsData.contexts) {
          contexts = contextsData.contexts;
        }
        
        console.log('📋 [URA-CREATE] Array de contextos:', contexts);
        setAvailableContexts(contexts);
        console.log(`✅ [URA-CREATE] ${contexts.length} contextos carregados:`, contexts);
      } catch (error) {
        console.error('⚠️ [URA-CREATE] Erro ao buscar contextos:', error);
      }
      
      // Verificar integração WhatsApp
      await checkWhatsAppIntegration(tenantId);
      
    } catch (error) {
      console.error('❌ [URA-CREATE] Erro ao carregar dados iniciais:', error);
    }
  };

  // Função para verificar se há integrações WhatsApp cadastradas
  const checkWhatsAppIntegration = async (tenant) => {
    try {
      console.log('🔍 [URA-CREATE] Verificando integrações WhatsApp para tenant:', tenant);
      
      const response = await getIntegrationsByAccountcode(tenant);
      
      if (response && response.success && response.data && response.data.length > 0) {
        setHasWhatsAppIntegration(true);
        console.log('✅ [URA-CREATE] Integrações WhatsApp encontradas:', response.data.length);
      } else {
        setHasWhatsAppIntegration(false);
        console.log('⚠️ [URA-CREATE] Nenhuma integração WhatsApp encontrada');
      }
    } catch (error) {
      console.error('❌ [URA-CREATE] Erro ao verificar integrações WhatsApp:', error);
      setHasWhatsAppIntegration(false);
    }
  };

  const handleChange = (field, value) => {
    // Aplicar normalização para context_name
    if (field === 'context_name') {
      // Normalizar: lowercase, remover espaços, manter apenas letras, números e hífen
      const normalizedValue = value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')  // Remove acentos
        .replace(/[^a-z0-9-]/g, '')        // Remove tudo exceto letras, números e hífen
        .replace(/\s+/g, '');              // Remove espaços
      
      setValues(prev => ({ ...prev, [field]: normalizedValue }));
    } else {
      setValues(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleMenuChange = (menuConfig) => {
    console.log('🔄 [URA-CREATE] MenuConfig alterado:', menuConfig);
    setValues(prev => {
      const newValues = { ...prev, ...menuConfig };
      console.log('📝 [URA-CREATE] Novos valores após menu change:', newValues);
      return newValues;
    });
  };

  const handleExtensionsChange = (newExtensions) => {
    setValues(prev => ({ ...prev, extensions: newExtensions }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('🔍 [URA-CREATE] Dados a serem enviados:', values);
    
    // Validação frontend
    const validation = URAApi.validateURAConfig(values);
    if (!validation.isValid) {
      // Exibir todos os erros de validação
      const errorMessage = validation.errors.length === 1 
        ? validation.errors[0]
        : `Corrija os seguintes erros:\n${validation.errors.map((e, i) => `${i + 1}. ${e}`).join('\n')}`;
      
      showAlert('danger', errorMessage);
      console.error('❌ [URA-CREATE] Validação falhou:', validation.errors);
      return;
    }
    
    try {
      setLoading(true);
      
      // Formatar dados para API
      const formattedData = URAApi.formatConfigDataForAPI(values);
      
      console.log('📤 [URA-CREATE] Enviando para API:', formattedData);
      
      const response = await URAApi.createURAConfig(formattedData);
      
      if (response.success) {
        console.log('✅ [URA-CREATE] URA criada com sucesso:', response.data);
        showAlert('success', 'URA criada com sucesso!');
        
        setTimeout(() => {
          history.push('/config/ura');
        }, 2000);
      } else {
        console.error('❌ [URA-CREATE] Erro ao criar URA:', response);
        showAlert('danger', response.error || 'Erro ao criar URA');
        
        if (response.details && Array.isArray(response.details)) {
          response.details.forEach(detail => {
            console.error('  -', detail.msg || detail);
          });
        }
      }
      
    } catch (error) {
      console.error('❌ [URA-CREATE] Erro ao criar URA:', error);
      showAlert('danger', 'Erro ao criar URA');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    // Scroll para o topo da página para mostrar o alert
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      setAlert({ show: false, type: '', message: '' });
    }, 5000);
  };

  const handleCancel = () => {
    history.push('/config/ura');
  };

  if (!userTenant) {
    return <Loading />;
  }

  return (
    <div className="ura-form-container">
      <div className="ura-form-header">
        <div className="ura-form-title-wrapper">
          <svg className="ura-form-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
          </svg>
          <div>
            <h1 className="ura-form-title">Nova URA (Unidade de Resposta Audível)</h1>
            <p className="ura-form-subtitle">Crie um menu interativo de atendimento</p>
          </div>
        </div>
        <button className="btn-back" onClick={handleCancel}>
          <FontAwesomeIcon icon={faArrowLeft} />
          Voltar
        </button>
      </div>

      {alert.show && (
        <div className="alerts-section">
          <div className={`alert-message alert-${alert.type}`}>
            <div className="alert-header">
              <span>{alert.type === 'success' ? 'Sucesso!' : alert.type === 'danger' ? 'Erro!' : 'Atenção!'}</span>
              <button className="alert-close" onClick={() => setAlert({ ...alert, show: false })}>×</button>
            </div>
            <div className="alert-body" style={{ whiteSpace: 'pre-line' }}>{alert.message}</div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-section-card">
          <h3 className="section-title">
            <FontAwesomeIcon icon={faInfoCircle} className="section-icon" />
            Informações Básicas
          </h3>
          
          <div className="form-field">
            <label className="form-label">Tenant</label>
            <input
              className="form-input"
              type="text"
              value={values.tenant}
              disabled
            />
          </div>
          
          <div className="form-field">
            <label className="form-label">Nome do Contexto *</label>
            <input
              className="form-input"
              type="text"
              placeholder={`ura-${userTenant}`}
              value={values.context_name}
              onChange={(e) => handleChange('context_name', e.target.value)}
              required
              pattern="[a-z0-9-]+"
              title="Apenas letras minúsculas, números e hífen (-)"
            />
            <p className="form-help">
              <strong>Formato:</strong> Apenas letras minúsculas, números e hífen (-). Sem espaços ou caracteres especiais.<br />
              <strong>Padrão gerado:</strong> ura-{'{nome-tenant}'}-{values.tenant}
            </p>
          </div>
          
          <div className="form-field">
            <label className="form-label">Descrição</label>
            <textarea
              className="form-textarea"
              placeholder="Ex: Menu principal de atendimento"
              value={values.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows="2"
            />
          </div>
        </div>

        <MenuConfiguration
          config={values}
          onChange={handleMenuChange}
          tenant={userTenant}
          hasWhatsAppIntegration={hasWhatsAppIntegration}
          availableQueues={availableQueues}
          availableContexts={availableContexts}
        />

        {/* Configuração de Music on Hold */}
        <div className="form-section-card">
          <MohConfiguration
            config={{
              moh_enabled: values.moh_enabled || false,
              moh_class: values.moh_class || ''
            }}
            tenant={userTenant}
            onChange={(mohConfig) => {
              setValues(prev => ({
                ...prev,
                moh_enabled: mohConfig.moh_enabled,
                moh_class: mohConfig.moh_class
              }));
            }}
            disabled={loading}
          />
        </div>

        <ExtensionManager
          extensions={values.extensions}
          onChange={handleExtensionsChange}
          tenant={userTenant}
          hasWhatsAppIntegration={hasWhatsAppIntegration}
          availableQueues={availableQueues}
          availableContexts={availableContexts}
        />

        <div className="form-section-card">
          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={handleCancel}>
              Cancelar
            </button>
            <button 
              type="submit" 
              className="btn-submit"
              disabled={loading}
            >
              {loading ? (
                <>Salvando...</>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} />
                  Criar Configuração
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default URACreate;

